import { Component } from 'react'
import { findDOMNode } from 'react-dom'
import scroll from 'scroll'
import ease from 'ease-component'
import { find, first, forEach, isElement, isNumber, last, result } from '../libraries/lodash'
import { detect } from 'detect-browser'
const browser = detect();

export const headerHeight = function () {
  const applicationContent = document.getElementById('application-header')
  const { height } = getBoundingClientRect(applicationContent)
  return height
}

export const getBoundingClientRect = function (el) {
  if (el) {
    let {
      bottom,
      height,
      left,
      right,
      top,
      width
    } = el.getBoundingClientRect()
    return { bottom, height, left, right, top, width }
  } else {
    return {}
  }
}

export const getTouchEventCoordinates = function (event) {
  return {
    x: result(event, 'changedTouches[0].screenX', 0),
    y: result(event, 'changedTouches[0].screenY', 0)
  }
}

export const scrollTo = function ({ pageId, id = null, element: _element = null, animate = false, useWindowScrollTo = false }) {
  const page = document.getElementById(pageId)
  if (page === null || (id === null && (_element === null || !isElement(_element)))) {
    return false
  }
  
  const isIdPosition = isNumber(id)
  const element = _element ?? (document.getElementById(id) || document.getElementById('#' + id))

  if (element || isIdPosition) {
    const { top } = getBoundingClientRect(element)
    const position = isIdPosition
      ? id
      : useWindowScrollTo
          ? top + window.pageYOffset - headerHeight()
          : top + page.scrollTop - headerHeight() - getScrollAdjust(element.parentElement)
    
    useWindowScrollTo
      ? window.scrollTo({
        top: position,
        behavior: animate ? 'smooth' : 'auto'
      })
      : scrollToHeight(
          page,
          position,
          animate
        )
  }

  return false
}

const collectScrollAdjustments = function (el) {
  if (!el) return []
  else if (el.classList.contains('scroll-adjust')) {
    return [el, ...collectScrollAdjustments(el.parentElement)]
  } else return collectScrollAdjustments(el.parentElement)
}

const getScrollAdjust = function (el) {
  return collectScrollAdjustments(el)
    .map(
      s =>
        getBoundingClientRect(s.querySelector('.scroll-adjust-height')).height
    )
    .reduce((a, h) => (h - 22 || 0) + a, 0)
}

const scrollToHeight = function (page, scrollTop, animate) {
  scrollTop = Math.round(scrollTop)
  if (scrollTop > page.scrollHeight || scrollTop === page.scrollTop) {
    return false
  }
  Object.assign(page, { scrollHistory: page.scrollHistory || [] })

  try {
    if (animate) {
      scroll.top(page, scrollTop, { duration: 400, ease: ease.outSine })
    } else {
      page.scrollTop = scrollTop
      page.scrollHistory.push(scrollTop)
    }
    return scrollTop
  } catch (e) {
    return false
  }
}

const matches = function (elm, selector) {
  try {
    const matches = (elm.document || elm.ownerDocument).querySelectorAll(
      selector
    )
    let i = matches.length
    while (--i >= 0 && matches.item(i) !== elm) {}
    return i > -1
  } catch (e) {
    return false
  }
}

export const updateContentHeight = function () {
  try {
    document.getElementById('application-content').style.top =
      headerHeight() + 'px'
  } catch (e) {}
}

export const scrollToNext = function (pageId, selector) {
  const page = document.getElementById(pageId)

  if (page === null) {
    return false
  }

  const stops = Array.of(...page.querySelectorAll(selector)).map(
    el => page.scrollTop + getBoundingClientRect(el).top
  )
  const next = find(stops, x => x > page.scrollTop) || first(stops)

  scroll.top(page, next, { duration: 400, ease: 'outSine' })
}

export const scrollToPrev = function (pageId, selector) {
  const page = document.getElementById(pageId)

  if (page === null) {
    return false
  }

  const stops = Array.of(...page.querySelectorAll(selector)).map(
    el => page.scrollTop + getBoundingClientRect(el).top
  )
  const next = last(stops.filter(x => x < page.scrollTop)) || last(stops)

  scroll.top(page, next, { duration: 400, ease: 'outSine' })
}

export const isParentOf = function (container, element) {
  if (container instanceof Component) {
    container = findDOMNode(container)
  }

  if (!element) {
    return false
  }

  if (element === container) {
    return true
  }

  return isParentOf(container, element.parentElement)
}

export const createElement = function (type, values = {}) {
  const element = document.createElement(type)
  forEach(values, (value, name) => element.setAttribute(name, value))
  return element
}

export const isElementAboveFold = function (el) {
  const { top } = getBoundingClientRect(el)
  const { height } = getBoundingClientRect(
    document.querySelector('.application')
  )
  return top < height
}

export const getParentElement = (el) => browser.name === 'ie'
  ? el.parentElement || el.parentNode
  : el.parentElement

export const triggerEventTarget = function (el, map = {}) {
  if (!el) {
    return false
  }
  const result = Object.keys(map).reduce((found, selector) => {
    if (!found && matches(el, selector)) {
      map[selector](el)
      return true
    }

    return found
  }, false)

  return (
    result ||
    triggerEventTarget(
      getParentElement(el),
      map
    )
  )
}

export const getPathTo = function (el) {
  const { parentElement, tagName } = el

  if (!parentElement) return tagName

  const index = [...parentElement.childNodes].indexOf(el)
  return getPathTo(parentElement) + `/${tagName}[${index}]`
}

export const getLink = function (el) {
  if (!el) {
    return false
  }
  if (el.nodeName === 'A') {
    return el
  }
  return getLink(getParentElement(el))
}

export const getBaseURI = function () {
  // Workaround for IE, which doesn't support relative base hrefs
  if (document.baseURI !== undefined) {
    return document.baseURI
  }
  const base = document.querySelector('base').getAttribute('href')
  return window.location.protocol + '//' + window.location.host + base
}
